import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { LineChart, Settings } from 'lucide-react'

interface TradingViewChartProps {
  symbol?: string
  theme?: 'light' | 'dark'
  height?: number
}

export const TradingViewChart = ({ 
  symbol = "XAUUSD", 
  theme = 'dark', 
  height = 500
}: TradingViewChartProps) => {
  const container = useRef<HTMLDivElement>(null)
  const [isLoading, setIsLoading] = useState(true)
  
  useEffect(() => {
    const widgetContainer = container.current
    if (!widgetContainer) return
    
    // Clear previous content
    widgetContainer.innerHTML = ''
    
    // ✅ Create the container div with explicit pixel height (no 100%)
    const widgetDiv = document.createElement('div')
    widgetDiv.id = 'tradingview_chart'
    widgetDiv.style.width = '100%'
    widgetDiv.style.height = `${height}px` // ✅ Pixel espliciti
    widgetDiv.style.overflow = 'hidden' // ✅ Evita spazio vuoto extra
    
    widgetContainer.appendChild(widgetDiv)
    
    // ✅ Load TradingView widget script
    const script = document.createElement('script')
    script.src = 'https://s3.tradingview.com/tv.js'
    script.async = true
    script.onload = () => {
      if ((window as any).TradingView) {
        new (window as any).TradingView.widget({
          symbol: `FX_IDC:${symbol}`,
          interval: "60",
          container_id: "tradingview_chart",
          width: "100%",
          height: height, // ✅ Valore numerico
          autosize: false, // ✅ Evita calcoli interni errati
          timezone: "Europe/Rome",
          theme: theme,
          style: "1",
          locale: "en",
          enable_publishing: false,
          hide_side_toolbar: false,
          allow_symbol_change: false,
          studies: [
            "BB@tv-basicstudies",
            "RSI@tv-basicstudies",
            "Volume@tv-basicstudies"
          ]
        })
      }
    }
    
    document.body.appendChild(script)
    
    // Hide loading state after script loads
    const timeout = setTimeout(() => setIsLoading(false), 2000)
    
    return () => {
      clearTimeout(timeout)
      if (widgetContainer) {
        widgetContainer.innerHTML = ''
      }
      if (script.parentNode) {
        document.body.removeChild(script)
      }
    }
  }, [symbol, theme, height])

  return (
    <div className="bg-card rounded-lg border shadow-sm">
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
            <span className="text-lg font-semibold">{symbol}</span>
          </div>
          
          <div className="flex items-center space-x-2 text-sm">
            <span className="text-muted-foreground">Live:</span>
            <span className="font-mono font-semibold">2,032.45</span>
            <span className="text-green-500">+0.45%</span>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm">
            <LineChart className="w-4 h-4 mr-2" />
            Indicators
          </Button>
          <Button variant="outline" size="sm">
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </Button>
        </div>
      </div>
      
      <div 
        className="relative"
        style={{ 
          height: `${height}px`, 
          width: '100%',
          overflow: 'hidden' // ✅ Evita spazio vuoto
        }}
      >
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-muted/50 z-10">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading TradingView Chart...</p>
            </div>
          </div>
        )}
        
        <div 
          ref={container}
          className="tradingview-widget-container"
          style={{ 
            width: '100%',
            height: `${height}px`, // ✅ Stesso valore numerico
            overflow: 'hidden'
          }}
        />
      </div>
      
      <div className="flex items-center justify-between p-3 border-t bg-muted/20">
        <div className="flex space-x-1">
          {['1m', '5m', '15m', '1h', '4h', '1D', '1W'].map((tf) => (
            <Button key={tf} variant="ghost" size="sm" className="h-8 px-3">
              {tf}
            </Button>
          ))}
        </div>
        
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <span>Powered by</span>
          <span className="font-semibold text-foreground">TradingView</span>
        </div>
      </div>
    </div>
  )
}
